import React from 'react';
import {
  Brain,
  Sparkles,
  BarChart3,
  Clock,
  ShieldCheck,
  MessageSquareText,
  Rocket,
  BookOpenCheck,
  BellRing,
  Layers3,
  Workflow,
  Users,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/common/Navbar';
import './PublicPages.css';

const mainFeatures = [
  {
    icon: Brain,
    title: 'Adaptive AI Tutor',
    description: "Suggests the next lesson based on each student's pace and weaker topics."
  },
  {
    icon: BarChart3,
    title: 'Live Progress Analytics',
    description: 'Chapter-wise performance tracking with clear trends and action points.'
  },
  {
    icon: Clock,
    title: 'Instant Evaluation',
    description: 'Quickly scores practice answers and mock responses with actionable feedback.'
  },
  {
    icon: MessageSquareText,
    title: 'Smart Revision Help',
    description: 'Provides concise revision prompts and explainers for high-priority topics.'
  },
  {
    icon: Sparkles,
    title: 'Exam-Focused Practice',
    description: 'Structures question patterns in exam style to sharpen preparation.'
  },
  {
    icon: ShieldCheck,
    title: 'Parent Friendly Tracking',
    description: 'Gives parents clear updates without unnecessary complexity.'
  },
  {
    icon: BookOpenCheck,
    title: 'Syllabus Mapped Learning',
    description: 'Keeps the learning journey mapped against syllabus outcomes.'
  },
  {
    icon: BellRing,
    title: 'Action Reminders',
    description: 'Sends smart nudges for low activity and pending tasks.'
  }
];

const workflow = [
  {
    title: 'Assess',
    description: "Detects the student's current level and sets a realistic starting point."
  },
  {
    title: 'Practice',
    description: 'Runs focused concept-and-question cycles in short daily sessions.'
  },
  {
    title: 'Improve',
    description: 'Uses a feedback loop to close weak areas and build confidence quickly.'
  }
];

const advancedCapabilities = [
  {
    icon: Layers3,
    title: 'Multi-Mode Study Engine',
    description:
      'Switch between class mode, revision mode, and exam simulation mode from one dashboard.'
  },
  {
    icon: Workflow,
    title: 'Continuous Improvement Loop',
    description:
      'Automatically reorders next-task priorities using performance data so effort is never wasted.'
  },
  {
    icon: Users,
    title: 'Shared Visibility for Family',
    description:
      'The parent view provides a clear summary: consistency, strong chapters, and urgent support areas.'
  },
  {
    icon: Rocket,
    title: 'Fast Onboarding',
    description:
      'Generates a learning path immediately after a new student registers, with no setup delay.'
  }
];

const FeaturesPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <>
      <Navbar />
      <main className="public-page">
        <div className="public-page-shell">
          <section className="public-hero-card">
            <span className="public-kicker">Features</span>
            <h1>Everything You Need to Study Better, Faster, and Smarter</h1>
            <p>
              ZapTick AI is designed as a complete study operating system where learning, practice,
              analytics, revision, and exam readiness work in one connected flow.
            </p>
          </section>

          <section className="public-stat-strip">
            <article className="public-stat-item">
              <h3>Daily Consistency</h3>
              <p>Students can maintain a routine through short guided actions.</p>
            </article>
            <article className="public-stat-item">
              <h3>Actionable Feedback</h3>
              <p>Along with raw scores, students get clear next steps, not just marks.</p>
            </article>
            <article className="public-stat-item">
              <h3>Exam Alignment</h3>
              <p>Practice formats are kept close to actual exam expectations.</p>
            </article>
            <article className="public-stat-item">
              <h3>Parent Confidence</h3>
              <p>A clear view of progress helps families provide smarter support at home.</p>
            </article>
          </section>

          <section className="public-section">
            <h2 className="public-section-title">Core Platform Features</h2>
            <p className="public-section-subtitle">
              These features are built for daily use so student momentum remains steady.
            </p>
          </section>

          <section className="public-grid">
            {mainFeatures.map((feature) => (
              <article key={feature.title} className="public-card">
                <div className="public-card-icon">
                  <feature.icon size={22} />
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </article>
            ))}
          </section>

          <section className="public-section section-gap-top">
            <h2 className="public-section-title">How the Learning Flow Works</h2>
            <p className="public-section-subtitle">
              The platform follows a simple 3-step cycle that keeps learning structured without overcomplication.
            </p>
          </section>

          <section className="process-grid">
            {workflow.map((step, index) => (
              <article key={step.title} className="process-card">
                <span className="process-step">Step {index + 1}</span>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </article>
            ))}
          </section>

          <section className="public-section section-gap-top">
            <h2 className="public-section-title">Advanced Capabilities</h2>
            <p className="public-section-subtitle">
              When deeper control is needed, the platform provides advanced modules.
            </p>
          </section>

          <section className="public-grid">
            {advancedCapabilities.map((feature) => (
              <article key={feature.title} className="public-card info-emphasis-card">
                <div className="public-card-icon">
                  <feature.icon size={22} />
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </article>
            ))}
          </section>

          <section className="public-section section-gap-top">
            <h2 className="public-section-title">What This Means for Students</h2>
          </section>

          <section className="public-columns">
            <article className="info-panel">
              <h3>Less Confusion, More Clarity</h3>
              <p>
                Students can clearly see the next best action each day. This keeps study time focused
                instead of random and improves confidence consistently.
              </p>
            </article>
            <article className="info-panel">
              <h3>Strong Exam Readiness</h3>
              <p>
                The combination of continuous practice and analysis reduces final-exam pressure and
                improves performance in practical terms.
              </p>
            </article>
          </section>

          <section className="public-cta-card">
            <h2>Try These Features by Creating Your Account</h2>
            <p>New users can register directly and start with a guided setup.</p>
            <button className="public-primary-btn" onClick={() => navigate('/register')}>
              Register Now
            </button>
          </section>
        </div>
      </main>
    </>
  );
};

export default FeaturesPage;
